import React, { useEffect, useState } from "react";
import { fetchData } from "../../services/FetchDataService";
import { useClient } from "../../context/ClientContext";
import { useTheme } from "../../context/ThemeContext";
import { looksLikeBase64, base64ToBlobUrl } from "../../utils/fileHelpers";

// Helper to safely get nested values (e.g. "phase_study.name")
function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object") {
      return acc[key];
    }
    return undefined;
  }, obj);
}

interface FieldItem {
  key: string;
  label: string;
  icon?: string;
}

interface StepCardProps {
  title: string;
  fields: FieldItem[];
  endpoint_get: string;
  filterID: number | string;
}

// Single Card for a "step"
const StepCard: React.FC<StepCardProps> = ({
  title,
  fields,
  endpoint_get,
  filterID,
}) => {
  const { theme } = useTheme();
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = useClient("CLINICAL_TRIAL_SERVICE");

  useEffect(() => {
    if (!filterID) return;
    const fetchStepData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchData(client, endpoint_get, {
          id: filterID,
        });
        if (response.code === 200 && response.data) {
          setFetchedData(response.data);
        } else {
          setError(`Error code: ${response.code}`);
        }
      } catch (err: any) {
        setError(err.message || "Unknown error");
      }
      setLoading(false);
    };
    fetchStepData();
  }, [filterID, endpoint_get]);

  const containerClass =
    theme === "night"
      ? "bg-gray-800 text-gray-100 border-gray-600"
      : "bg-white text-gray-800 border-gray-200";

  const cardClass =
    theme === "night"
      ? "bg-gray-700 border-gray-600 text-gray-100"
      : "bg-gray-50 border-gray-300 text-gray-700";

  if (loading || error || !fetchedData) {
    return (
      <div className={`p-5 border rounded-xl shadow-md ${containerClass}`}>
        <h3 className="text-lg font-bold mb-2 text-primary">{title}</h3>
        <p>{loading ? "Loading..." : "No data available."}</p>
      </div>
    );
  }

  const dataArray = Array.isArray(fetchedData) ? fetchedData : [fetchedData];

  return (
    <div
      className={`relative p-5 border rounded-xl shadow-md ${containerClass}`}
    >
      <div className="border-b border-gray-200 pb-2 mb-4">
        <h3 className="text-lg font-bold text-primary">{title}</h3>
      </div>

      {dataArray.map((item, idx) => (
        <div
          key={idx}
          className={`mb-4 p-4 border rounded-2xl shadow-sm ${cardClass}`}
        >
          <div className="text-sm font-semibold text-primary-500 mb-2">
            {title} {dataArray.length > 1 ? `#${idx + 1}` : ""}
          </div>

          <ul className="divide-y divide-gray-200 text-sm">
            {fields.map((fieldItem, index) => {
              let rawValue = getNestedValue(item, fieldItem.key);

              if (
                fieldItem.key === "document_path" ||
                fieldItem.key === "data_accessibility_variables_document_path"
              ) {
                const href = base64ToBlobUrl(rawValue); // memoise if you like
                rawValue = (
                  <a
                    href={href}
                    download={`clinical_trial_${filterID}_${idx}.pdf`}
                    className="text-primary-600 underline hover:text-primary-800"
                  >
                    Download file
                  </a>
                );
              }

              if (typeof rawValue === "boolean") {
                rawValue = rawValue ? (
                  <span className="text-green-500 font-bold">✅ Yes</span>
                ) : (
                  <span className="text-red-500 font-bold">❌ No</span>
                );
              } else if (
                rawValue === null ||
                rawValue === undefined ||
                rawValue === ""
              ) {
                rawValue = "N/A";
              }

              return (
                <li key={index} className="py-2">
                  <div className="flex items-center space-x-2">
                    {fieldItem.icon && (
                      <span className="material-icons text-primary-300">
                        {fieldItem.icon}
                      </span>
                    )}
                    <span className="font-semibold text-primary-300">
                      {fieldItem.label}
                    </span>
                  </div>
                  <div className="ml-8 mt-1">{rawValue}</div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

interface CreativeStepsLayoutProps {
  clinical_trial_id: number;
}

// Main Layout for read-only steps
const CreativeStepsLayout: React.FC<CreativeStepsLayoutProps> = ({
  clinical_trial_id,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { theme } = useTheme();

  const steps = [
    {
      title: "Clinical Trial",
      endpoint_get: "/clinical-trial/get-by-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "scientific_title",
          label: "Scientific Title",
          icon: "description",
        },
        { key: "public_title", label: "Public Title", icon: "public" },
        {
          key: "scientific_acronym",
          label: "Scientific Acronym",
          icon: "emoji_symbols",
        },
        {
          key: "protocol_number",
          label: "Protocol Number",
          icon: "confirmation_number",
        },
        {
          key: "protocol_version",
          label: "Protocol Version",
          icon: "library_books",
        },
        {
          key: "registration_number",
          label: "Registration Number",
          icon: "app_registration",
        },
        {
          key: "registration_status.name",
          label: "Registration Status",
          icon: "flag",
        },
      ],
    },
    {
      title: "Study Purpose & Design",
      endpoint_get: "/study-purpose-design/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        { key: "purpose_study", label: "Purpose Study", icon: "psychology" },
        {
          key: "objective_study",
          label: "Objective Study",
          icon: "track_changes",
        },
        {
          key: "study_hypothesis",
          label: "Study Hypothesis",
          icon: "help_outline",
        },
        { key: "type_of_study.name", label: "Type of Study", icon: "category" },
        { key: "phase_study.name", label: "Phase Study", icon: "science" },
        { key: "intervention", label: "Intervention", icon: "healing" },
        {
          key: "randomization_method_name",
          label: "Randomization Method",
          icon: "shuffle",
        },
        {
          key: "control_group_name",
          label: "Control Group Name",
          icon: "group",
        },
        {
          key: "masking_description",
          label: "Masking Description",
          icon: "visibility_off",
        },
        {
          key: "study_design_control_group_presence",
          label: "Control Group Presence?",
          icon: "fact_check",
        },
        {
          key: "study_design_masking_status",
          label: "Masking Status?",
          icon: "visibility_off",
        },
      ],
    },
    {
      title: "Study Population Eligibility",
      endpoint_get: "/study-population-eligibility/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "health_condition_studied",
          label: "Health Condition Studied",
          icon: "medical_services",
        },
        {
          key: "type_of_eligibility.name",
          label: "Type of Eligibility",
          icon: "fact_check",
        },
        {
          key: "eligibility_description",
          label: "Eligibility Description",
          icon: "description",
        },
        {
          key: "target_number_participant",
          label: "Target Number Participant",
          icon: "person_search",
        },
        {
          key: "final_number_participant",
          label: "Final Number Participant",
          icon: "people",
        },
        { key: "sample_size", label: "Sample Size", icon: "straighten" },
      ],
    },
    {
      title: "Study Timeline Location",
      endpoint_get: "/study-timeline-location/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "study_duration",
          label: "Study Duration(Years)",
          icon: "timelapse",
        },
        { key: "country.name", label: "Country Recruitment", icon: "public" },
        {
          key: "location_study_site",
          label: "Study Site Location",
          icon: "location_on",
        },
        {
          key: "study_participating_center_physical_address",
          label: "Center Physical Address",
          icon: "location_on",
        },
        {
          key: "study_participating_center_postal_address",
          label: "Center Postal Address",
          icon: "location_on",
        },
        { key: "region.name", label: "Center Region", icon: "flag" },
        {
          key: "anticipated_start_date",
          label: "Anticipated Start Date",
          icon: "event",
        },
        {
          key: "anticipated_end_date",
          label: "Anticipated End Date",
          icon: "event_busy",
        },
        {
          key: "recruitment_status.name",
          label: "Recruitment Status",
          icon: "history_toggle_off",
        },
      ],
    },
    {
      title: "Contact Investigator",
      endpoint_get: "/contact-investigator/list-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "investigator_role.name",
          label: "Investigator Role ID",
          icon: "person",
        },
        {
          key: "institution.name",
          label: "Institution ID",
          icon: "account_balance",
        },
        { key: "country.name", label: "Country ID", icon: "public" },
        { key: "name", label: "Name", icon: "account_circle" },
        { key: "mobile_number", label: "Mobile Number", icon: "call" },
        { key: "email_address", label: "Email Address", icon: "email" },
        { key: "postal_address", label: "Postal Address", icon: "location_on" },
        { key: "city", label: "City", icon: "location_city" },
      ],
    },
    {
      title: "Ethical Regulatory Approval",
      endpoint_get: "/ethical-regulatory-approval/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "ethical_regulatory.name_ethical_authority",
          label: "Ethical Regulatory",
          icon: "gavel",
        },
        {
          key: "is_approved_ethic_committee",
          label: "Approved by Ethics Committee?",
          icon: "check_circle",
        },
        {
          key: "document_number",
          label: "Document Number",
          icon: "description",
        },
        { key: "document_path", label: "Document Path", icon: "folder" },
      ],
    },
    {
      title: "Funding Sponsorship",
      endpoint_get: "/funding-sponsorship/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "name",
          label: "Funder/Sponsor Name",
          icon: "account_balance_wallet",
        },
        { key: "amount", label: "Amount", icon: "attach_money" },
        { key: "country.name", label: "Country", icon: "public" },
        { key: "funding_sector.name", label: "Funding Sector", icon: "domain" },
      ],
    },
    {
      title: "Study Description (Lay & Scientific)",
      endpoint_get:
        "/study-description-scientific-public/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        { key: "study_website", label: "Study Website", icon: "language" },
        { key: "lay_summary", label: "Lay Summary", icon: "article" },
        {
          key: "scientific_summary",
          label: "Scientific Summary",
          icon: "science",
        },
      ],
    },
    {
      title: "Study Intervention & Outcome",
      endpoint_get: "/study-intervention-outcome/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "intervention_name",
          label: "Intervention Name",
          icon: "build_circle",
        },
        {
          key: "intervention_description",
          label: "Intervention Description",
          icon: "notes",
        },
        {
          key: "control_comparator",
          label: "Control Comparator",
          icon: "compare",
        },
        {
          key: "type_of_outcome.name",
          label: "Type of Outcome",
          icon: "label",
        },
        {
          key: "outcome_description",
          label: "Outcome Description",
          icon: "description",
        },
      ],
    },
    {
      title: "Study Result Publication",
      endpoint_get: "/study-result-publication/list-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        { key: "summary_results", label: "Summary Results", icon: "summarize" },
        {
          key: "permission_to_publish",
          label: "Permission to Publish?",
          icon: "check_circle",
        },
        {
          key: "authority_committee_name",
          label: "Authority Committee Name",
          icon: "business",
        },
        {
          key: "publication_type.name",
          label: "Publication Type",
          icon: "category",
        },
        { key: "publisher.name", label: "Publisher", icon: "account_balance" },
        { key: "publication_title", label: "Publication Title", icon: "title" },
        { key: "url_doi", label: "URL/DOI", icon: "link" },
      ],
    },
    {
      title: "Open Data Access",
      endpoint_get: "/open-data-access/get-by-clinical-trial-id",
      size: "col-span-12 row-span-2",
      fields: [
        {
          key: "repository_name",
          label: "Repository Name",
          icon: "folder_open",
        },
        {
          key: "statistical_method_used",
          label: "Statistical Method Used",
          icon: "calculate",
        },
        {
          key: "study_identification_variable.name",
          label: "Study Identification Variable",
          icon: "badge",
        },
        {
          key: "statistically_significant_p_value.name",
          label: "Significant P-Value",
          icon: "grade",
        },
        {
          key: "effect_size_metric",
          label: "Effect Size Metric",
          icon: "straighten",
        },
        {
          key: "effect_size_value",
          label: "Effect Size Value",
          icon: "exposure",
        },
        {
          key: "confidence_interval",
          label: "Confidence Interval",
          icon: "percent",
        },
        {
          key: "adjustment_multiple_comparison",
          label: "Adjustment Multiple Comparison",
          icon: "tune",
        },
        {
          key: "handling_missing_data",
          label: "Handling Missing Data",
          icon: "help_outline",
        },
        {
          key: "data_accessibility_variables_document_path",
          label: "Doc Path for Accessibility Vars",
          icon: "folder",
        },
        {
          key: "quality_assessment_variable",
          label: "Quality Assessment Variable",
          icon: "fact_check",
        },
        {
          key: "risk_of_bias_assessment",
          label: "Risk of Bias Assessment",
          icon: "warning",
        },
        {
          key: "study_limitation",
          label: "Study Limitation",
          icon: "error_outline",
        },
        {
          key: "funding_source",
          label: "Funding Source",
          icon: "account_balance",
        },
        {
          key: "potential_conflict_interest",
          label: "Potential Conflict of Interest",
          icon: "report_problem",
        },
        {
          key: "additional_variable",
          label: "Additional Variable",
          icon: "note_add",
        },
        {
          key: "publication_bias_indicator",
          label: "Publication Bias Indicator",
          icon: "visibility_off",
        },
        {
          key: "heterogeneity_measure",
          label: "Heterogeneity Measure",
          icon: "thermostat",
        },
        {
          key: "sensitivity_analysis_result",
          label: "Sensitivity Analysis Result",
          icon: "analytics",
        },
        { key: "allow_publish", label: "Allow Publish", icon: "publish" },
      ],
    },
  ];

  return (
    <div
      className={`p-8 rounded-3xl min-h-screen ${
        theme === "night" ? "bg-gray-700 text-gray-100" : "bg-primary-100"
      }`}
    >
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {steps.map((step, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === index
                ? "bg-primary-600 text-white"
                : theme === "night"
                ? "bg-gray-800 text-primary-400 border border-primary-600"
                : "bg-white text-primary-600 border border-primary-600"
            }`}
          >
            {step.title}
          </button>
        ))}
      </div>

      {/* Step content */}
      <div
        className={
          theme === "night"
            ? "bg-gray-800 p-5 rounded-3xl"
            : "bg-gray-50 p-5 rounded-3xl"
        }
      >
        <StepCard
          key={activeTab}
          title={steps[activeTab].title}
          fields={steps[activeTab].fields}
          endpoint_get={steps[activeTab].endpoint_get}
          filterID={clinical_trial_id}
        />
      </div>
    </div>
  );
};

export default CreativeStepsLayout;
